import { VOICE_WEBRTC_REMOTE_MEDIA_ELEMENT_ID } from '../config/voiceWebRtcConfig';
import { getVoiceAgentState } from './voiceAgentSessionStore';
import { isAgentCallDead, isPeerConnectionIceReady } from './voiceJsSipSessionStatus';
import { collectAgentRtcAudioStats, logAgentRtcAudioStats } from './voiceIceMediaWatchdog';
import { logWebRtcEvent } from './voiceWebRtcLog';

/** @type {MediaStream|null} */
let remotePlaybackStream = null;

/** @type {HTMLAudioElement|null} */
let remoteAudioElementRef = null;

/** @type {AudioContext|null} */
let agentAudioUnlockContext = null;

/** Silent track so play() succeeds during the Answer click (empty streams fail autoplay). */
/** @type {MediaStreamTrack|null} */
let silentUnlockTrack = null;

let lastAudioSyncLogAt = 0;
let remoteAudioPlayingLogged = false;
const remoteAudioUnmuteBoundTracks = new WeakSet();

/** @type {string|null} */
let remoteAudioAttachedSessionId = null;

/** Invalidates in-flight remote-audio retry loops. */
let remoteAudioScheduleToken = 0;

/** @type {ReturnType<typeof setTimeout>|null} */
let remoteAudioScheduleTimer = null;

function resolveRemoteMediaElementId(elementId) {
  return elementId
    || getVoiceAgentState().agentRemoteMediaElementId
    || VOICE_WEBRTC_REMOTE_MEDIA_ELEMENT_ID;
}

function styleRemoteAudioElement(element) {
  const audioElement = element;
  audioElement.autoplay = true;
  audioElement.playsInline = true;
  audioElement.setAttribute('playsinline', 'true');
  audioElement.muted = false;
  audioElement.volume = 1;
  audioElement.controls = false;
  // display:none / hidden can block WebRTC playback in Chromium.
  audioElement.style.cssText = 'position:fixed;left:0;bottom:0;width:1px;height:1px;opacity:0.01;pointer-events:none;z-index:-1;';
}

function getRemotePlaybackStream() {
  if (!remotePlaybackStream) {
    remotePlaybackStream = new MediaStream();
  }
  return remotePlaybackStream;
}

function ensureAudioUnlockContext() {
  const AudioContextClass = window.AudioContext || window.webkitAudioContext;
  if (!AudioContextClass) return null;
  if (!agentAudioUnlockContext || agentAudioUnlockContext.state === 'closed') {
    agentAudioUnlockContext = new AudioContextClass();
  }
  if (agentAudioUnlockContext.state === 'suspended') {
    agentAudioUnlockContext.resume().catch(() => {});
  }
  return agentAudioUnlockContext;
}

function ensureSilentUnlockTrack() {
  const stream = getRemotePlaybackStream();
  const hasLiveRemote = stream.getAudioTracks().some(
    (track) => track !== silentUnlockTrack && track.readyState === 'live'
  );
  if (hasLiveRemote) return;
  if (silentUnlockTrack && silentUnlockTrack.readyState === 'live') {
    if (!stream.getAudioTracks().includes(silentUnlockTrack)) {
      stream.addTrack(silentUnlockTrack);
    }
    return;
  }
  const context = ensureAudioUnlockContext();
  if (!context) return;
  const destination = context.createMediaStreamDestination();
  const oscillator = context.createOscillator();
  const gain = context.createGain();
  gain.gain.value = 0.00001;
  oscillator.connect(gain);
  gain.connect(destination);
  oscillator.start();
  const [track] = destination.stream.getAudioTracks();
  if (!track) return;
  silentUnlockTrack = track;
  stream.addTrack(track);
}

function removeSilentUnlockTrack() {
  if (!silentUnlockTrack) return;
  const stream = remotePlaybackStream;
  if (stream) {
    try {
      stream.removeTrack(silentUnlockTrack);
    } catch {
      // ignore
    }
  }
  try {
    silentUnlockTrack.stop();
  } catch {
    // ignore
  }
  silentUnlockTrack = null;
}

function ensureRemoteAudioElement(elementId) {
  const id = resolveRemoteMediaElementId(elementId);
  let element = remoteAudioElementRef && document.contains(remoteAudioElementRef)
    ? remoteAudioElementRef
    : document.getElementById(id);
  if (!element) {
    element = document.createElement('audio');
    element.id = id;
    document.body.appendChild(element);
  }
  remoteAudioElementRef = element;
  const stream = getRemotePlaybackStream();
  if (element.srcObject !== stream) {
    element.srcObject = stream;
  }
  styleRemoteAudioElement(element);
  return element;
}

/**
 * Capture the Answer-click user gesture so later WebRTC play() is allowed.
 */
export function unlockAgentRemoteAudio() {
  window.stopRingtone?.();
  ensureAudioUnlockContext();
  ensureSilentUnlockTrack();
  const element = ensureRemoteAudioElement();
  const playPromise = element.play();
  if (!playPromise?.then) return;
  playPromise.then(() => {
    logWebRtcEvent('sdk.remoteAudioUnlocked', { paused: element.paused });
  }).catch((error) => {
    logWebRtcEvent('sdk.remoteAudioUnlockFailed', {
      message: error?.message ?? String(error)
    });
  });
}

function playRemoteAudioElement(element, reason) {
  if (!element) return;
  styleRemoteAudioElement(element);
  if (!element.paused && !element.ended) {
    return;
  }
  const playPromise = element.play();
  if (!playPromise?.then) return;
  playPromise.then(() => {
    if (remoteAudioPlayingLogged) return;
    remoteAudioPlayingLogged = true;
    // Element play ≠ RTP. Do not treat this as proof of call media.
    logWebRtcEvent('sdk.remoteAudioPlaying', {
      reason,
      paused: element.paused,
      muted: element.muted,
      volume: element.volume,
      srcTracks: element.srcObject?.getAudioTracks?.().length ?? 0,
      note: 'element_play_only_not_rtp'
    });
  }).catch((error) => {
    logWebRtcEvent('sdk.remoteAudioPlayFailed', {
      reason,
      message: error?.message ?? String(error)
    });
  });
}

function describeMediaTracks(pc) {
  const receivers = pc?.getReceivers?.() ?? [];
  const senders = pc?.getSenders?.() ?? [];
  return {
    receivers: receivers
      .filter((entry) => entry.track?.kind === 'audio')
      .map((entry) => ({
        muted: entry.track.muted,
        enabled: entry.track.enabled,
        readyState: entry.track.readyState
      })),
    senders: senders
      .filter((entry) => entry.track?.kind === 'audio')
      .map((entry) => ({
        muted: entry.track.muted,
        enabled: entry.track.enabled,
        readyState: entry.track.readyState
      }))
  };
}

function logRtcAudioStats(session, reason) {
  logAgentRtcAudioStats(session, reason).catch(() => {});
}

export function logCurrentAgentRtcAudioStats(reason) {
  logRtcAudioStats(getVoiceAgentState().currentRtcSession, reason);
}

export { collectAgentRtcAudioStats };

function attachRemoteTrackToElement(track, element, streams, reason) {
  if (!track || track.kind !== 'audio' || !element) return;
  if (track.readyState === 'ended') {
    logWebRtcEvent('sdk.remoteTrackSkipped', { reason, readyState: 'ended' });
    return;
  }
  const audioTrack = track;
  const audioElement = element;
  audioTrack.enabled = true;
  const playbackStream = getRemotePlaybackStream();
  if (audioElement.srcObject !== playbackStream) {
    audioElement.srcObject = playbackStream;
  }
  playbackStream.getAudioTracks().forEach((existing) => {
    if (existing.id !== audioTrack.id && existing.readyState === 'ended') {
      playbackStream.removeTrack(existing);
    }
  });
  const alreadyBound = playbackStream.getAudioTracks().some(
    (existing) => existing.id === audioTrack.id
  );
  if (!alreadyBound) {
    playbackStream.addTrack(audioTrack);
    removeSilentUnlockTrack();
    audioElement.srcObject = null;
    audioElement.srcObject = playbackStream;
    logWebRtcEvent('sdk.remoteTrackAttached', {
      reason,
      muted: audioTrack.muted,
      enabled: audioTrack.enabled,
      readyState: audioTrack.readyState,
      usedEventStream: Boolean(streams?.[0])
    });
  }
  if (!remoteAudioUnmuteBoundTracks.has(audioTrack)) {
    remoteAudioUnmuteBoundTracks.add(audioTrack);
    audioTrack.addEventListener('unmute', () => {
      playRemoteAudioElement(audioElement, `${reason}.unmute`);
    });
  }
  playRemoteAudioElement(audioElement, reason);
}

export function attachPeerConnectionDiagnostics(session) {
  const state = getVoiceAgentState();
  const bind = (pc) => {
    const peerConnection = pc;
    if (!peerConnection || peerConnection.__mercatelyVoiceDiag) return;
    peerConnection.__mercatelyVoiceDiag = true;
    const logState = (label) => {
      logWebRtcEvent('session.pcState', {
        id: session.id,
        label,
        connectionState: peerConnection.connectionState,
        iceConnectionState: peerConnection.iceConnectionState,
        iceGatheringState: peerConnection.iceGatheringState
      });
    };
    peerConnection.addEventListener('connectionstatechange', () => {
      logState('connectionstatechange');
      if (peerConnection.connectionState !== 'connected') return;
      if (state.currentRtcSession !== session) return;
      syncAgentRemoteAudioPlayback();
      logRtcAudioStats(session, 'connection.connected');
    });
    peerConnection.addEventListener('iceconnectionstatechange', () => {
      logState('iceconnectionstatechange');
      if (peerConnection.iceConnectionState === 'failed') {
        logWebRtcEvent('session.iceFailed', {
          id: session.id,
          note: 'JsSIP will map this to RTP Timeout and terminate'
        });
      }
      if (
        peerConnection.iceConnectionState !== 'connected'
        && peerConnection.iceConnectionState !== 'completed'
      ) {
        return;
      }
      if (state.currentRtcSession !== session) return;
      syncAgentRemoteAudioPlayback();
      logRtcAudioStats(session, 'ice.connected');
      [2000, 5000, 8000].forEach((delayMs) => {
        setTimeout(() => {
          if (state.currentRtcSession === session) {
            logRtcAudioStats(session, `ice.connected+${delayMs / 1000}s`);
          }
        }, delayMs);
      });
    });
    peerConnection.addEventListener('icegatheringstatechange', () => logState('icegatheringstatechange'));
    peerConnection.addEventListener('icecandidateerror', (event) => {
      logWebRtcEvent('session.iceCandidateError', {
        id: session.id,
        errorCode: event.errorCode,
        errorText: event.errorText,
        url: event.url,
        address: event.address,
        port: event.port,
        note: event.errorCode === 701
          ? 'TURN/STUN unreachable from this browser (allocate failed)'
          : undefined
      });
    });
    peerConnection.addEventListener('icecandidate', (event) => {
      if (!event.candidate || !event.candidate.candidate) {
        logWebRtcEvent('session.iceCandidate', { id: session.id, type: 'end-of-candidates' });
        return;
      }
      const line = event.candidate.candidate;
      const typeMatch = /\btyp\s+(\w+)\b/i.exec(line);
      const protocolMatch = /\b(udp|tcp)\b/i.exec(line);
      logWebRtcEvent('session.iceCandidate', {
        id: session.id,
        type: typeMatch?.[1] ?? event.candidate.type ?? 'unknown',
        protocol: protocolMatch?.[1]?.toLowerCase()
          ?? event.candidate.protocol
          ?? null,
        address: event.candidate.address || null,
        port: event.candidate.port || null
      });
    });
  };
  if (session.connection) {
    bind(session.connection);
  }
  session.on('peerconnection', (data) => {
    bind(data.peerconnection);
  });
}

function stopScheduledRemoteAudioPlayback() {
  remoteAudioScheduleToken += 1;
  if (remoteAudioScheduleTimer) {
    clearTimeout(remoteAudioScheduleTimer);
    remoteAudioScheduleTimer = null;
  }
}

function isRemoteAudioHealthy(elementId) {
  const element = remoteAudioElementRef
    || document.getElementById(resolveRemoteMediaElementId(elementId));
  if (!element || element.paused || element.ended) return false;
  const session = getVoiceAgentState().currentRtcSession;
  // Live track + element playing is not enough — ICE must be connected (RTP may still
  // be zero; the ICE media watchdog fails the call if bytes stay at 0).
  if (!isPeerConnectionIceReady(session)) return false;
  const hasLiveRemote = element.srcObject?.getAudioTracks?.().some(
    (track) => track.readyState === 'live' && track !== silentUnlockTrack
  );
  return Boolean(hasLiveRemote);
}

export function releaseRemoteAudioElement() {
  stopScheduledRemoteAudioPlayback();
  remoteAudioAttachedSessionId = null;
  lastAudioSyncLogAt = 0;
  remoteAudioPlayingLogged = false;
  removeSilentUnlockTrack();
  const stream = remotePlaybackStream;
  if (stream) {
    stream.getTracks().forEach((track) => {
      try {
        stream.removeTrack(track);
      } catch {
        // ignore
      }
    });
  }
  const element = remoteAudioElementRef || document.getElementById(
    resolveRemoteMediaElementId()
  );
  if (!element) return;
  try {
    element.pause();
    if (stream) {
      element.srcObject = stream;
    }
  } catch {
    // ignore
  }
}

export function bindPeerConnectionRemoteAudio(pc, element, reason = 'bind') {
  if (!pc || !element) return;
  const audioElement = element;
  const peerConnection = pc;
  const receivers = peerConnection.getReceivers?.() ?? [];
  const audioReceiver = receivers.find((entry) => entry.track?.kind === 'audio');
  if (audioReceiver?.track) {
    attachRemoteTrackToElement(audioReceiver.track, audioElement, null, reason);
  } else {
    logWebRtcEvent('sdk.remoteAudioWaiting', {
      reason,
      ...describeMediaTracks(peerConnection)
    });
  }

  if (peerConnection.__mercatelyVoiceRemoteAudio) return;
  peerConnection.__mercatelyVoiceRemoteAudio = true;
  peerConnection.addEventListener('track', (event) => {
    attachRemoteTrackToElement(event.track, audioElement, event.streams, 'pc.track');
  });
}

export function attachRemoteAudio(session, elementId) {
  const rtcSession = session;
  const element = ensureRemoteAudioElement(elementId);
  if (rtcSession.__mercatelyVoiceRemoteAudioAttached) {
    if (rtcSession.connection) {
      bindPeerConnectionRemoteAudio(rtcSession.connection, element, 'attach.existingPc');
    }
    return;
  }
  rtcSession.__mercatelyVoiceRemoteAudioAttached = true;
  lastAudioSyncLogAt = 0;
  const remoteStream = session.connection?.getRemoteStreams?.()?.[0];
  if (remoteStream?.getAudioTracks?.().length) {
    attachRemoteTrackToElement(remoteStream.getAudioTracks()[0], element, [remoteStream], 'getRemoteStreams');
  }
  if (session.connection) {
    bindPeerConnectionRemoteAudio(session.connection, element, 'attach.existingPc');
  }
  session.on('peerconnection', (data) => {
    bindPeerConnectionRemoteAudio(data.peerconnection, element, 'session.peerconnection');
  });
  session.on('track', (event) => {
    attachRemoteTrackToElement(event.track, element, event.streams, 'session.track');
  });
  session.on('confirmed', () => {
    logWebRtcEvent('session.confirmed', { id: session.id, via: 'event' });
    if (session.connection) {
      bindPeerConnectionRemoteAudio(session.connection, element, 'session.confirmed');
    }
    playRemoteAudioElement(element, 'session.confirmed');
  });
}

export function resetRemoteAudioAttachmentTracking() {
  remoteAudioAttachedSessionId = null;
}

export function playRemoteMediaElement(elementId) {
  syncAgentRemoteAudioPlayback(elementId);
}

/**
 * Binds the current JsSIP session remote audio to the hidden <audio> element.
 *
 * @param {string} [elementId]
 * @returns {boolean}
 */
export function syncAgentRemoteAudioPlayback(elementId) {
  const id = resolveRemoteMediaElementId(elementId);
  const session = getVoiceAgentState().currentRtcSession;
  if (isAgentCallDead(session)) {
    stopScheduledRemoteAudioPlayback();
    return false;
  }
  if (isRemoteAudioHealthy(id)) {
    stopScheduledRemoteAudioPlayback();
    return true;
  }
  const element = ensureRemoteAudioElement(id);
  if (remoteAudioAttachedSessionId !== session.id) {
    attachRemoteAudio(session, id);
    remoteAudioAttachedSessionId = session.id;
  } else if (session.connection) {
    bindPeerConnectionRemoteAudio(session.connection, element, 'sync.retry');
  }
  if (session.connection && isPeerConnectionIceReady(session)) {
    const now = Date.now();
    if (now - lastAudioSyncLogAt > 4000) {
      lastAudioSyncLogAt = now;
      logWebRtcEvent('sdk.audioSync', {
        id: session.id,
        ice: session.connection.iceConnectionState,
        conn: session.connection.connectionState,
        ...describeMediaTracks(session.connection)
      });
    }
    playRemoteAudioElement(element, 'sync');
  }
  if (isRemoteAudioHealthy(id)) {
    stopScheduledRemoteAudioPlayback();
  }
  return true;
}

/**
 * Retries remote audio bind (ICE/DTLS often completes after call_answered).
 *
 * @param {string} [elementId]
 * @param {{ attempts?: number, intervalMs?: number }} [options]
 */
export function scheduleAgentRemoteAudioPlayback(elementId, options = {}) {
  const id = resolveRemoteMediaElementId(elementId);
  const attempts = options.attempts ?? 15;
  const intervalMs = options.intervalMs ?? 2000;
  if (isAgentCallDead(getVoiceAgentState().currentRtcSession)) {
    stopScheduledRemoteAudioPlayback();
    return;
  }
  if (isRemoteAudioHealthy(id)) {
    stopScheduledRemoteAudioPlayback();
    return;
  }
  if (remoteAudioScheduleTimer) return;
  const token = remoteAudioScheduleToken;
  let count = 0;

  const tick = () => {
    if (token !== remoteAudioScheduleToken) return;
    if (
      isAgentCallDead(getVoiceAgentState().currentRtcSession)
      || isRemoteAudioHealthy(id)
    ) {
      stopScheduledRemoteAudioPlayback();
      return;
    }

    syncAgentRemoteAudioPlayback(id);
    count += 1;

    if (
      isAgentCallDead(getVoiceAgentState().currentRtcSession)
      || isRemoteAudioHealthy(id)
      || count >= attempts
    ) {
      stopScheduledRemoteAudioPlayback();
      return;
    }
    remoteAudioScheduleTimer = setTimeout(tick, intervalMs);
  };

  tick();
}
